import type { BlobStorage, DocStorage } from '@affine/nbstore';
import type { Workspace } from '@blocksuite/affine/store';
import { Service } from '@toeverything/infra';

import type {
  CreationLocationRequest,
  DocCreationLocationService,
} from '../../doc-location/service';
import type { WorkspaceFlavoursService } from './flavours';
import type { WorkspaceRepositoryService } from './repo';

export class WorkspaceFactoryService extends Service {
  constructor(
    private readonly flavoursService: WorkspaceFlavoursService,
    private readonly locationService: DocCreationLocationService,
    private readonly workspaceRepository: WorkspaceRepositoryService
  ) {
    super();
  }

  /**
   * create workspace
   * @param flavour workspace flavour
   * @param initial callback to put initial data to workspace
   * @returns workspace id
   */
  create = async (
    flavour: string,
    initial: (
      docCollection: Workspace,
      blobFrontend: BlobStorage,
      docFrontend: DocStorage
    ) => Promise<void> = () => Promise.resolve(),
    options: { captureLocation?: boolean } = {}
  ) => {
    const provider = this.flavoursService.flavours$.value.find(
      x => x.flavour === flavour
    );
    if (!provider) {
      throw new Error(`Unknown workspace flavour: ${flavour}`);
    }
    const docIds = new Set<string>();
    let pending: CreationLocationRequest | undefined;
    const metadata = await provider.createWorkspace(
      async (collection, blobs, docs) => {
        // Initial imports use a temporary collection instead of DocsService.
        // Subscribe only while initializing this new workspace, never on load.
        const subscription =
          options.captureLocation !== false && this.locationService.enabled
            ? collection.meta.docMetaAdded.subscribe(id => {
                docIds.add(id);
                pending ??= this.locationService.beginCapture();
              })
            : undefined;
        try {
          await initial(collection, blobs, docs);
        } finally {
          subscription?.unsubscribe();
        }
      }
    );
    if (pending && docIds.size && this.locationService.enabled) {
      const request = pending;
      void request.coordinates
        .then(async location => {
          if (!location || !this.locationService.enabled) return;
          const ref = this.workspaceRepository.open({ metadata });
          try {
            await this.locationService.captureForDocs(
              ref.workspace,
              [...docIds],
              request
            );
          } finally {
            ref.dispose();
          }
        })
        .catch(() => {});
    }
    return metadata;
  };
}
